import { isAxiosError, type AxiosInstance } from "axios";
import { computed, inject, ref, watch, type App, type Ref } from "vue";
import type { RouteLocationNormalized, Router } from "vue-router";

export const authKey = 'auth'

export type AuthConfig = {
    router: Router
    axios: AxiosInstance
    refresh_interval: number
}



type Auth = {
    user: <User extends object>() => Ref<User | null>
    is_ready: Ref<boolean>
    login: (data: any) => Promise<void>
    register: (data: any) => Promise<void>
    fetch: <User extends object>() => Promise<User>
    refresh: () => Promise<void>
    logout: () => Promise<void>
    check: (roles?: string[]) => boolean,
    install: (app: App, key?: string) => void
}

export function createAuth(config: AuthConfig) {

    const user = ref<any | null>(null)
    const is_ready = ref<boolean>(false)

    config.axios.interceptors.response.use((config) => {
        return config
    }, (error) => {
        if (isAxiosError(error)) {
            if ([403, 401].includes(error.response?.status || 0) && config.router.currentRoute.value.path != '/login') {
                config.router.push('/login')
            }

        }
        return Promise.reject(error)
    })

    watch(user, () => {
        if (null === user.value && config.router.currentRoute.value.path !== '/login') {
            config.router.push('/login')
        }
        if (null !== user.value && config.router.currentRoute.value.path === '/login') {
            config.router.push('/')
        }
    })


    const fetch = async<User extends object>(): Promise<User> => {
        try {
            const { data } = await config.axios.get<User>(`auth/user`)
            user.value = data
            return data
        } catch (error) {
            user.value = null
            if (isAxiosError(error)) {
                throw new Error(error.response?.data.error)
            } else if (error instanceof Error) {
                throw error
            }
            throw new Error("Não foi possível efetuar o login")
        }
    }

    const login = async ({ data: body }: { data: any }) => {
        try {
            await config.axios.post(`auth/login`, body)
            await fetch()
        } catch (error) {
            if (isAxiosError(error)) {
                throw new Error(error.response?.data.error)
            } else if (error instanceof Error) {
                throw error
            }
            throw new Error("Não foi possível efetuar o login")
        }
    }

    const register = async ({ data: body }: { data: any }) => {
        try {
            await config.axios.post(`auth/register`, body)
            await fetch()
        } catch (error) {
            if (isAxiosError(error)) {
                throw new Error(error.response?.data.error)
            } else if (error instanceof Error) {
                throw error
            }
            throw new Error("Não foi possível efetuar o registro")
        }
    }

    const logout = async () => {
        try {
            await config.axios.post(`auth/logout`)
            user.value = null
        } catch (error) {
            if (isAxiosError(error)) {
                throw new Error(error.response?.data.error)
            } else if (error instanceof Error) {
                throw error
            }
            throw new Error("Não foi possível efetuar o login")
        }
    }

    const refresh = async () => {
        try {
            if (user.value) {
                await config.axios.post(`auth/refresh`)
                await fetch()
            }

            setTimeout(refresh, (config.refresh_interval || 30) * 1000)
        } catch (error) {
            if (isAxiosError(error)) {
                throw new Error(error.response?.data.error)
            } else if (error instanceof Error) {
                throw error
            }
            throw new Error("Não foi possível efetuar o login")
        }
    }

    function getAuthMeta({ matched }: RouteLocationNormalized) {
        // eslint-disable-next-line functional/no-let
        let auth
        const authRoutes = matched.filter(({ meta }) =>
            Object.prototype.hasOwnProperty.call(meta, "auth")
        )

        // matches the nested route, the last one in the list
        if (authRoutes.length) {
            auth = authRoutes[authRoutes.length - 1].meta.auth
        }

        return auth ?? null
    }
      

    const check = (roles?: string[]) => {
        if (!user.value) {
            return false
        }

        if (roles && !roles.includes(user.value.tipo)) {
            return false
        }

        return true
    }

    config.router.beforeEach((to, _from, next) => {
        
        
        const authMeta: any = getAuthMeta(to)
        

        let authRedirect: string = authMeta?.redirect ?? '/login'
        const roles: string[] = authMeta?.roles ?? undefined

        
        if (!auth.check(roles) && to.path != '/login') {
            next(authRedirect)
            return
        }
        next()
    })

    refresh().then(() => {
        is_ready.value = true
    }).catch(() => {})

    const auth: Auth = {
        user<User extends object>(): Ref<User | null> {
            return user
        },
        is_ready,
        login,
        register,
        refresh,
        fetch,
        logout,
        check,
        install: (app: App, key: string = authKey) => {
            app.config.globalProperties.$auth = auth
            app.provide(key, auth)
            
        }
    }

    return auth
}

export function useAuth(key: symbol | string = authKey) {
    return inject(key) as Auth
}

export function useUser<User extends object>(key: symbol | string = authKey): Ref<User | null> {
    const auth = useAuth(key)
    return auth.user()
}